// src/components/forms/InputDetailFormBuilder.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    Grid,
    Button,
    TextField,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Checkbox,
    IconButton,
    Alert,
    Chip,
    Divider,
    Tabs,
    Tab,
    alpha,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ArrowBack as BackIcon,
    CloudUpload as UploadIcon,
    Code as CodeIcon,
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import AdminLayout from '../pages/AdminLayout';

const FIELD_TYPES = [
    { value: 'text', label: 'Short Text', icon: '📝' },
    { value: 'textarea', label: 'Long Text (Paragraph)', icon: '📄' },
    { value: 'email', label: 'Email', icon: '📧' },
    { value: 'phone', label: 'Phone Number', icon: '📱' },
    { value: 'number', label: 'Number', icon: '🔢' },
    { value: 'url', label: 'Website URL', icon: '🌐' },
    { value: 'date', label: 'Date', icon: '📅' },
    { value: 'time', label: 'Time', icon: '⏰' },
    { value: 'datetime', label: 'Date & Time', icon: '📆' },
    { value: 'dropdown', label: 'Dropdown Select', icon: '📋' },
    { value: 'radio', label: 'Radio Buttons', icon: '⚫' },
    { value: 'checkbox', label: 'Checkboxes', icon: '☑️' },
    { value: 'file', label: 'File Upload', icon: '📎' },
];

export default function InputDetailFormBuilder() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const [isPublished, setIsPublished] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [category, setCategory] = useState('general');
    const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
    const [jsonInput, setJsonInput] = useState('');
    const [editingFieldIndex, setEditingFieldIndex] = useState(null);
    const [status, setStatus] = useState('inactive');
    const [tabValue, setTabValue] = useState(0);

    const [newField, setNewField] = useState({
        label: '',
        fieldType: 'text',
        required: true,
        placeholder: '',
        helpText: '',
        defaultValue: '',
        options: [],
        validation: {
            minLength: null,
            maxLength: null,
            min: null,
            max: null,
            pattern: '',
        },
    });

    useEffect(() => {
        if (formId && formId !== 'new') {
            fetchForm();
        }
    }, [formId]);

    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                const data = formDoc.data();
                setFormTitle(data.title || '');
                setFormDescription(data.description || '');
                setCategory(data.category || 'general');
                setAllowMultipleSubmissions(data.allowMultipleSubmissions ?? false);
                setRedirectUrl(data.redirectUrl || '');
                setFields(data.fields || []);
                setStatus(data.status || 'inactive');
                setIsPublished(data.isPublished ?? false);
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveForm = async () => {
        try {
            setError('');
            setSuccess('');

            if (!formTitle.trim()) {
                setError('Form title is required');
                return;
            }

            if (fields.length === 0) {
                setError('Add at least one field');
                return;
            }

            setLoading(true);

            const formData = {
                title: formTitle.trim(),
                description: formDescription.trim(),
                formType: 'input-detail',
                category: category,
                allowMultipleSubmissions: allowMultipleSubmissions,
                redirectUrl: redirectUrl,
                fields: fields,
                status: status,
                isPublished: isPublished,
                isLocked: false,
                updatedAt: serverTimestamp(),
            };

            if (formId && formId !== 'new') {
                await updateDoc(doc(db, 'forms', formId), formData);
                setSuccess('Form updated successfully!');
            } else {
                const newFormId = `input_${Date.now()}`;
                await setDoc(doc(db, 'forms', newFormId), {
                    ...formData,
                    createdAt: serverTimestamp(),
                });
                setSuccess('Form created successfully!');
            }

            setTimeout(() => {
                navigate('/admin/forms');
            }, 1500);

        } catch (err) {
            console.error('Error saving form:', err);
            setError('Failed to save form: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(jsonInput);

            if (!parsed.title || !Array.isArray(parsed.fields)) {
                setError('Invalid JSON format. Must include title and fields array.');
                return;
            }

            setFormTitle(parsed.title || '');
            setFormDescription(parsed.description || '');
            setCategory(parsed.category || 'general');
            setAllowMultipleSubmissions(parsed.allowMultipleSubmissions ?? false);
            setRedirectUrl(parsed.redirectUrl || '');
            setStatus(parsed.status || 'inactive');
            setIsPublished(parsed.isPublished ?? false);
            setFields(parsed.fields || []);

            setJsonDialogOpen(false);
            setSuccess('Form imported successfully!');
            setError('');

        } catch (err) {
            setError('Invalid JSON format: ' + err.message);
        }
    };

    const exportToJson = () => {
        const formJson = {
            title: formTitle,
            description: formDescription,
            formType: 'input-detail',
            category: category,
            allowMultipleSubmissions: allowMultipleSubmissions,
            redirectUrl: redirectUrl,
            status: status,
            isPublished: isPublished,
            fields: fields,
        };

        const jsonString = JSON.stringify(formJson, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${formTitle.replace(/\s+/g, '_')}_input-detail.json`;
        link.click();
    };

    const handleAddField = () => {
        setError('');

        if (!newField.label.trim()) {
            setError('Field label is required');
            return;
        }

        if (['dropdown', 'radio', 'checkbox'].includes(newField.fieldType) &&
            (!newField.options || newField.options.length === 0)) {
            setError('Add at least one option for this field type');
            return;
        }

        if (editingFieldIndex !== null) {
            const updated = [...fields];
            updated[editingFieldIndex] = newField;
            setFields(updated);
        } else {
            setFields([...fields, newField]);
        }

        setNewField({
            label: '',
            fieldType: 'text',
            required: true,
            placeholder: '',
            helpText: '',
            defaultValue: '',
            options: [],
            validation: {
                minLength: null,
                maxLength: null,
                min: null,
                max: null,
                pattern: '',
            },
        });
        setEditingFieldIndex(null);
        setDialogOpen(false);
    };

    const handleEditField = (index) => {
        setNewField(fields[index]);
        setEditingFieldIndex(index);
        setDialogOpen(true);
        setTabValue(0);
    };

    const handleDeleteField = (index) => {
        if (window.confirm('Delete this field?')) {
            setFields(fields.filter((_, i) => i !== index));
        }
    };

    const handleAddOption = () => {
        setNewField({
            ...newField,
            options: [...(newField.options || []), { label: '', value: '' }],
        });
    };

    const handleOptionChange = (index, field, value) => {
        const updated = [...(newField.options || [])];
        updated[index] = { ...updated[index], [field]: value };
        setNewField({ ...newField, options: updated });
    };

    const handleDeleteOption = (index) => {
        if ((newField.options || []).length <= 1) {
            setError('At least one option is required');
            return;
        }
        const updated = (newField.options || []).filter((_, i) => i !== index);
        setNewField({ ...newField, options: updated });
    };

    return (
        <AdminLayout title="Form Builder">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
                    {error && (
                        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {/* Header */}
                    <Box sx={{ mb: 4 }}>
                        <Button
                            startIcon={<BackIcon />}
                            onClick={() => navigate('/admin/forms')}
                            sx={{ mb: 2 }}
                        >
                            Back to Forms
                        </Button>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                            📝 {formId && formId !== 'new' ? 'Edit' : 'Create'} Input Detail Form
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create data collection forms similar to Google Forms
                        </Typography>
                    </Box>

                    {/* Form Details */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            📋 Form Settings
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Form Title *"
                                    value={formTitle}
                                    onChange={(e) => setFormTitle(e.target.value)}
                                    placeholder="e.g., Startup Registration Form"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Form Description"
                                    value={formDescription}
                                    onChange={(e) => setFormDescription(e.target.value)}
                                    placeholder="Brief description of this form"
                                    multiline
                                    rows={3}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        label="Category"
                                    >
                                        <MenuItem value="general">General</MenuItem>
                                        <MenuItem value="registration">Registration</MenuItem>
                                        <MenuItem value="application">Application</MenuItem>
                                        <MenuItem value="survey">Survey</MenuItem>
                                        <MenuItem value="contact">Contact</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        label="Status"
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                        <MenuItem value="draft">Draft</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Redirect URL (Optional)"
                                    value={redirectUrl}
                                    onChange={(e) => setRedirectUrl(e.target.value)}
                                    placeholder="https://example.com/thank-you"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isPublished}
                                                onChange={(e) => setIsPublished(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={isPublished ? '✅ Published' : '🔒 Unpublished'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={allowMultipleSubmissions}
                                                onChange={(e) => setAllowMultipleSubmissions(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label="Allow Multiple Submissions"
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider sx={{ my: 4 }} />

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/admin/forms')}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveForm}
                            disabled={loading}
                            sx={{
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #d97edb 0%, #d94a5e 100%)',
                                },
                            }}
                        >
                            {loading ? 'Saving...' : 'Save Form'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<CodeIcon />}
                            onClick={() => setJsonDialogOpen(true)}
                            color="secondary"
                        >
                            Import JSON
                        </Button>
                        {fields.length > 0 && (
                            <Button
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                onClick={exportToJson}
                                color="success"
                            >
                                Export JSON
                            </Button>
                        )}
                    </Box>

                    {/* Fields Section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                🗂️ Form Fields ({fields.length})
                            </Typography>
                            {fields.length > 0 && (
                                <Chip
                                    label={`${fields.filter(f => f.required).length} Required Fields`}
                                    color="error"
                                    variant="outlined"
                                />
                            )}
                        </Box>

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => {
                                setNewField({
                                    label: '',
                                    fieldType: 'text',
                                    required: true,
                                    placeholder: '',
                                    helpText: '',
                                    defaultValue: '',
                                    options: [],
                                    validation: {
                                        minLength: null,
                                        maxLength: null,
                                        min: null,
                                        max: null,
                                        pattern: '',
                                    },
                                });
                                setEditingFieldIndex(null);
                                setDialogOpen(true);
                                setTabValue(0);
                                setError('');
                            }}
                            sx={{
                                mb: 2,
                                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            }}
                        >
                            Add Field
                        </Button>

                        <Grid container spacing={2}>
                            {fields.map((field, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            borderLeft: `4px solid ${field.required ? '#f44336' : '#f093fb'}`,
                                        }}
                                    >
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                                                        {index + 1}. {field.label}
                                                    </Typography>
                                                    {field.helpText && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            💡 {field.helpText}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    {field.required && (
                                                        <Chip
                                                            label="Required"
                                                            size="small"
                                                            color="error"
                                                        />
                                                    )}
                                                    <Chip
                                                        label={FIELD_TYPES.find(t => t.value === field.fieldType)?.icon + ' ' +
                                                            (FIELD_TYPES.find(t => t.value === field.fieldType)?.label || field.fieldType)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>

                                            {field.placeholder && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                    Placeholder: "{field.placeholder}"
                                                </Typography>
                                            )}

                                            {['dropdown', 'radio', 'checkbox'].includes(field.fieldType) && field.options && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Options: {field.options.map(opt => opt.label || opt.value || opt).join(', ')}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </CardContent>
                                        <CardActions>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditField(index)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteField(index)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        {fields.length === 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    py: 6,
                                    textAlign: 'center',
                                    backgroundColor: alpha('#f093fb', 0.05),
                                    border: '2px dashed',
                                    borderColor: alpha('#f093fb', 0.2),
                                    borderRadius: 2,
                                }}
                            >
                                <Typography variant="h6" color="text.secondary">
                                    No fields added yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Click "Add Field" to start building your form
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* Field Dialog */}
                    <Dialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            {editingFieldIndex !== null ? 'Edit Field' : 'Add Field'}
                        </DialogTitle>
                        <DialogContent>
                            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                                <Tab label="Basic" />
                                <Tab label="Options" />
                                <Tab label="Validation" />
                            </Tabs>

                            {/* Tab 0: Basic */}
                            {tabValue === 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Field Label *"
                                        value={newField.label}
                                        onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                                        placeholder="e.g., Company Name"
                                    />

                                    <FormControl fullWidth>
                                        <InputLabel>Field Type</InputLabel>
                                        <Select
                                            value={newField.fieldType}
                                            onChange={(e) => setNewField({ ...newField, fieldType: e.target.value })}
                                            label="Field Type"
                                        >
                                            {FIELD_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.icon} {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        label="Placeholder Text"
                                        value={newField.placeholder}
                                        onChange={(e) => setNewField({ ...newField, placeholder: e.target.value })}
                                        placeholder="Hint text shown in the field"
                                    />

                                    <TextField
                                        fullWidth
                                        label="Help Text"
                                        value={newField.helpText}
                                        onChange={(e) => setNewField({ ...newField, helpText: e.target.value })}
                                        placeholder="Additional information or instructions"
                                        multiline
                                        rows={2}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Default Value"
                                        value={newField.defaultValue}
                                        onChange={(e) => setNewField({ ...newField, defaultValue: e.target.value })}
                                        placeholder="Pre-filled value (optional)"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={newField.required}
                                                onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                                            />
                                        }
                                        label="Required Field"
                                    />
                                </Box>
                            )}

                            {/* Tab 1: Options */}
                            {tabValue === 1 && ['dropdown', 'radio', 'checkbox'].includes(newField.fieldType) && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                        Add options for users to select
                                    </Typography>
                                    {(newField.options || []).map((option, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <TextField
                                                fullWidth
                                                label={`Label ${idx + 1}`}
                                                value={typeof option === 'object' ? option.label : option}
                                                onChange={(e) => handleOptionChange(idx, 'label', e.target.value)}
                                                size="small"
                                            />
                                            <TextField
                                                fullWidth
                                                label={`Value ${idx + 1}`}
                                                value={typeof option === 'object' ? option.value : option}
                                                onChange={(e) => handleOptionChange(idx, 'value', e.target.value)}
                                                size="small"
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteOption(idx)}
                                                color="error"
                                                disabled={(newField.options || []).length === 1}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button
                                        onClick={handleAddOption}
                                        fullWidth
                                        startIcon={<AddIcon />}
                                        variant="outlined"
                                    >
                                        Add Option
                                    </Button>
                                </Box>
                            )}

                            {/* Tab 2: Validation */}
                            {tabValue === 2 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    {['text', 'textarea'].includes(newField.fieldType) && (
                                        <>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Minimum Length"
                                                value={newField.validation?.minLength || ''}
                                                onChange={(e) => setNewField({
                                                    ...newField,
                                                    validation: { ...newField.validation, minLength: parseInt(e.target.value) || null }
                                                })}
                                                placeholder="e.g., 3"
                                                inputProps={{ min: 0 }}
                                            />
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Maximum Length"
                                                value={newField.validation?.maxLength || ''}
                                                onChange={(e) => setNewField({
                                                    ...newField,
                                                    validation: { ...newField.validation, maxLength: parseInt(e.target.value) || null }
                                                })}
                                                placeholder="e.g., 100"
                                                inputProps={{ min: 0 }}
                                            />
                                        </>
                                    )}

                                    {newField.fieldType === 'number' && (
                                        <>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Minimum Value"
                                                value={newField.validation?.min || ''}
                                                onChange={(e) => setNewField({
                                                    ...newField,
                                                    validation: { ...newField.validation, min: parseInt(e.target.value) || null }
                                                })}
                                                placeholder="e.g., 0"
                                            />
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label="Maximum Value"
                                                value={newField.validation?.max || ''}
                                                onChange={(e) => setNewField({
                                                    ...newField,
                                                    validation: { ...newField.validation, max: parseInt(e.target.value) || null }
                                                })}
                                                placeholder="e.g., 100"
                                            />
                                        </>
                                    )}

                                    {newField.fieldType === 'file' && (
                                        <>
                                            <TextField
                                                fullWidth
                                                label="Accepted File Types"
                                                value={newField.validation?.accept || ''}
                                                onChange={(e) => setNewField({
                                                    ...newField,
                                                    validation: { ...newField.validation, accept: e.target.value }
                                                })}
                                                placeholder="e.g., .pdf,.doc,.docx"
                                                helperText="Comma-separated file extensions"
                                            />
                                            <TextField
                                                fullWidth
                                                label="Maximum File Size"
                                                value={newField.validation?.maxSize || ''}
                                                onChange={(e) => setNewField({
                                                    ...newField,
                                                    validation: { ...newField.validation, maxSize: e.target.value }
                                                })}
                                                placeholder="e.g., 5MB"
                                            />
                                        </>
                                    )}

                                    <Alert severity="info">
                                        Validation rules help ensure data quality
                                    </Alert>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setDialogOpen(false); setError(''); }}>
                                Cancel
                            </Button>
                            <Button onClick={handleAddField} variant="contained">
                                {editingFieldIndex !== null ? 'Update' : 'Add'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* JSON Import Dialog */}
                    <Dialog
                        open={jsonDialogOpen}
                        onClose={() => setJsonDialogOpen(false)}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>Import Input Detail Form from JSON</DialogTitle>
                        <DialogContent>
                            <TextField
                                fullWidth
                                multiline
                                rows={15}
                                value={jsonInput}
                                onChange={(e) => setJsonInput(e.target.value)}
                                placeholder="Paste your JSON here..."
                                variant="outlined"
                                sx={{ mt: 2, fontFamily: 'monospace' }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                📝 Paste a valid input detail form JSON. See documentation for format.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setJsonDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleImportJson} variant="contained" color="primary">
                                Import
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Paper>
            </Container>
        </AdminLayout>
    );
}
